/**
 * Plan editor — edits `system/planConfig` so per-tier LIMITS AND
 * MARKETING CONTENT can be tuned live without a code redeploy.
 *
 * What you can edit (all live, propagates within ~1s):
 *   • Per-tier LIMITS (member cap, project cap, storage cap)
 *   • Per-tier LABEL — overrides the hardcoded "Studio" / "Studio+"
 *     etc. display name shown on the mobile Plans page
 *   • Per-tier TAGLINE — one-line value-prop under the tier name
 *   • Per-tier FEATURES list — the bullet-checklist inside each card
 *
 * What you CAN'T edit here:
 *   • Tier KEYS (free / solo / studio / studio_plus / agency) — those
 *     are stored on every org doc as `subscription.tier`, renaming
 *     would require a migration
 *   • Sales PRICES — InteriorOS moved off in-app payments to a B2B
 *     sales-led model (Razorpay invoices per studio). The price
 *     strings shown below this header are informational only — they
 *     anchor your sales conversations, not anything the app shows
 *
 * Fallback safety: every content field is OPTIONAL. Leaving Label /
 * Tagline blank, or Features empty, makes the mobile app fall back
 * to the hardcoded copy in `src/features/billing/limits.ts` +
 * `app/(app)/plans.tsx`. A half-filled save can never brick the
 * Plans page.
 *
 * Saving calls `adminUpdatePlanConfig` which writes to
 * `system/planConfig` + appends to `adminAudit`. The change is live
 * for new actions immediately.
 */
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Divider,
  Group,
  NumberInput,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import { callAdmin, db } from '@/lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';

type TierKey = 'free' | 'solo' | 'studio' | 'studio_plus' | 'agency';
type TierConfig = {
  // Required limits
  maxMembers: number;
  maxProjects: number;
  maxStorageBytes: number;
  // Optional content fields — when blank, mobile falls back to the
  // hardcoded copy in `src/features/billing/limits.ts` +
  // `app/(app)/plans.tsx`. Edit any of these and the mobile Plans
  // page updates within ~1 second across every active app.
  label?: string;
  tagline?: string;
  features?: string[];
};
type PlanConfig = Record<TierKey, TierConfig>;

const TIERS: TierKey[] = ['free', 'solo', 'studio', 'studio_plus', 'agency'];

const TIER_LABELS: Record<TierKey, string> = {
  free: 'Free',
  solo: 'Solo',
  studio: 'Studio',
  studio_plus: 'Studio+',
  agency: 'Agency',
};

// Display-only pricing — shown ONLY in the admin portal for the App
// Owner's reference. Mobile app shows no prices (B2B sales-led).
// Real billing happens via Razorpay invoices sent during sales calls,
// at whatever amount you negotiate per studio.
const TIER_DISPLAY_PRICE: Record<TierKey, string> = {
  free: '₹0',
  solo: '₹599 / mo',
  studio: '₹999 / mo',
  studio_plus: '₹1,999 / mo',
  agency: '₹3,999 / mo',
};

export function Plans() {
  const [config, setConfig] = useState<PlanConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Live snapshot — changes from another admin (or another tab)
  // surface here too.
  useEffect(() => {
    const ref = doc(db, 'system', 'planConfig');
    return onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setError('system/planConfig is missing — run the seed once.');
          return;
        }
        const data = snap.data() as Record<string, TierConfig>;
        // Bootstrap default for studio_plus if the doc was written
        // before the tier existed. Falls back to Studio's limits +
        // 50% more seats — sensible starting point that the App
        // Owner can immediately re-tune from the admin UI without
        // a Cloud Function deploy.
        const studio_plus =
          data.studio_plus
          ?? (data.studio
            ? {
                maxMembers: Math.max(
                  data.studio.maxMembers === -1 ? -1 : Math.round(data.studio.maxMembers * 2.5),
                  data.studio.maxMembers,
                ),
                maxProjects: data.studio.maxProjects,
                maxStorageBytes: data.studio.maxStorageBytes * 2,
              }
            : { maxMembers: 15, maxProjects: -1, maxStorageBytes: 100 * 1024 ** 3 });
        // Normalise content fields: missing → empty defaults so the
        // form inputs render bound values (not "undefined" strings).
        // Saving back maps empties → undefined so the doc stays clean.
        const normalize = (t: TierConfig | undefined): TierConfig => ({
          maxMembers: t?.maxMembers ?? 0,
          maxProjects: t?.maxProjects ?? 0,
          maxStorageBytes: t?.maxStorageBytes ?? 0,
          label: t?.label ?? '',
          tagline: t?.tagline ?? '',
          features: Array.isArray(t?.features) ? t.features : [],
        });
        const next: PlanConfig = {
          free: normalize(data.free),
          solo: normalize(data.solo),
          studio: normalize(data.studio),
          studio_plus: normalize(studio_plus),
          agency: normalize(data.agency),
        };
        setConfig(next);
      },
      (err) => setError(err.message),
    );
  }, []);

  // Limit + content update helpers. Separate signatures so TS
  // catches accidentally passing a string to maxMembers etc.
  const updateLimit = (
    tier: TierKey,
    key: 'maxMembers' | 'maxProjects' | 'maxStorageBytes',
    value: number,
  ) => {
    if (!config) return;
    setConfig({ ...config, [tier]: { ...config[tier], [key]: value } });
  };
  const updateContent = (
    tier: TierKey,
    key: 'label' | 'tagline',
    value: string,
  ) => {
    if (!config) return;
    setConfig({ ...config, [tier]: { ...config[tier], [key]: value } });
  };
  const updateFeature = (tier: TierKey, idx: number, value: string) => {
    if (!config) return;
    const features = [...(config[tier].features ?? [])];
    features[idx] = value;
    setConfig({ ...config, [tier]: { ...config[tier], features } });
  };
  const addFeature = (tier: TierKey) => {
    if (!config) return;
    const features = [...(config[tier].features ?? []), ''];
    setConfig({ ...config, [tier]: { ...config[tier], features } });
  };
  const removeFeature = (tier: TierKey, idx: number) => {
    if (!config) return;
    const features = (config[tier].features ?? []).filter((_, i) => i !== idx);
    setConfig({ ...config, [tier]: { ...config[tier], features } });
  };

  const onSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      // Clean payload: trim strings, drop blank features, omit empty
      // content fields so the doc stays minimal and the mobile
      // fallback kicks in for unset values.
      const cleanedEntries = (Object.entries(config) as [TierKey, TierConfig][]).map(
        ([tier, t]) => {
          const cleaned: TierConfig = {
            maxMembers: t.maxMembers,
            maxProjects: t.maxProjects,
            maxStorageBytes: t.maxStorageBytes,
          };
          const label = t.label?.trim();
          if (label) cleaned.label = label;
          const tagline = t.tagline?.trim();
          if (tagline) cleaned.tagline = tagline;
          const features = (t.features ?? [])
            .map((f) => f.trim())
            .filter((f) => f.length > 0);
          if (features.length > 0) cleaned.features = features;
          return [tier, cleaned] as const;
        },
      );
      const payload = Object.fromEntries(cleanedEntries) as PlanConfig;
      await callAdmin.updatePlanConfig(payload);
      notifications.show({
        title: 'Plans saved',
        message: 'Live across the app within a second.',
        color: 'green',
      });
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: (err as Error).message,
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Plans</Title>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          onClick={onSave}
          loading={saving}
          disabled={!config}
        >
          Save changes
        </Button>
      </Group>

      <Alert
        icon={<IconAlertCircle size={16} />}
        color="blue"
        variant="light"
      >
        <Text size="sm">
          Per-tier <b>limits</b>, <b>label</b>, <b>tagline</b>, and{' '}
          <b>features list</b> are editable here and live across every active
          mobile app within ~1 second of saving. Leaving label / tagline blank
          (or features empty) makes the app fall back to its built-in copy.
          <br />
          <br />
          The price strings on each tier are <b>not</b> shown to users — the
          mobile app is on a B2B sales-led model. Use them only as your own
          reference when sending Razorpay invoices.
        </Text>
      </Alert>

      {error ? <Text c="red">{error}</Text> : null}

      {config ? (
        <Stack gap="md">
          {TIERS.map((tier) => (
            <Card key={tier} withBorder p="md">
              <Group justify="space-between" mb="sm">
                <Group gap="sm">
                  <Title order={4}>{TIER_LABELS[tier]}</Title>
                  <Badge variant="light">{TIER_DISPLAY_PRICE[tier]}</Badge>
                </Group>
                <Text c="dimmed" size="xs">
                  Use <Text span ff="monospace">-1</Text> for unlimited.
                </Text>
              </Group>

              {/* ── LIMITS ─────────────────────────────────────── */}
              <Text size="xs" fw={600} c="dimmed" mb={6}>
                LIMITS
              </Text>
              <Group gap="md" align="flex-end">
                <NumberInput
                  label="Members"
                  value={config[tier].maxMembers}
                  onChange={(v) => updateLimit(tier, 'maxMembers', Number(v))}
                  min={-1}
                  step={1}
                  w={140}
                />
                <NumberInput
                  label="Projects"
                  value={config[tier].maxProjects}
                  onChange={(v) => updateLimit(tier, 'maxProjects', Number(v))}
                  min={-1}
                  step={1}
                  w={140}
                />
                <NumberInput
                  label="Storage (bytes)"
                  description={
                    config[tier].maxStorageBytes >= 0
                      ? humanBytes(config[tier].maxStorageBytes)
                      : 'unlimited'
                  }
                  value={config[tier].maxStorageBytes}
                  onChange={(v) =>
                    updateLimit(tier, 'maxStorageBytes', Number(v))
                  }
                  min={-1}
                  step={1_073_741_824}
                  thousandSeparator=","
                  w={240}
                />
              </Group>

              <Divider my="md" />

              {/* ── CONTENT (label / tagline / features) ───────── */}
              <Text size="xs" fw={600} c="dimmed" mb={6}>
                MARKETING CONTENT
              </Text>
              <Stack gap="sm">
                <TextInput
                  label="Display label (optional)"
                  placeholder={TIER_LABELS[tier]}
                  description={`Leave blank to use the built-in label "${TIER_LABELS[tier]}".`}
                  value={config[tier].label ?? ''}
                  onChange={(e) => updateContent(tier, 'label', e.currentTarget.value)}
                  maxLength={40}
                />
                <Textarea
                  label="Tagline (optional)"
                  placeholder="e.g. For solo designers — unlimited projects"
                  description="One-line value-prop shown under the tier name. Leave blank to use the built-in tagline."
                  value={config[tier].tagline ?? ''}
                  onChange={(e) => updateContent(tier, 'tagline', e.currentTarget.value)}
                  maxLength={120}
                  autosize
                  minRows={1}
                  maxRows={3}
                />

                {/* Features editor — add / remove rows */}
                <Stack gap={4}>
                  <Group justify="space-between" align="center">
                    <Text size="sm" fw={500}>
                      Feature list (optional)
                    </Text>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconPlus size={14} />}
                      onClick={() => addFeature(tier)}
                    >
                      Add feature
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Bullet list shown in the tier card. Leave empty to use the
                    built-in feature list. Lines ending with{' '}
                    <Text span ff="monospace">:</Text> render as section dividers
                    (e.g. <Text span ff="monospace">Everything in Studio, plus:</Text>).
                  </Text>
                  {(config[tier].features ?? []).length === 0 ? (
                    <Text size="xs" c="dimmed" fs="italic">
                      No features set. The mobile app will use the built-in list.
                    </Text>
                  ) : null}
                  {(config[tier].features ?? []).map((feature, i) => (
                    <Group key={i} gap="xs" align="center">
                      <TextInput
                        value={feature}
                        onChange={(e) =>
                          updateFeature(tier, i, e.currentTarget.value)
                        }
                        placeholder={`Feature ${i + 1}`}
                        style={{ flex: 1 }}
                        maxLength={80}
                      />
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        onClick={() => removeFeature(tier, i)}
                        aria-label={`Remove feature ${i + 1}`}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>
      ) : null}
    </Stack>
  );
}

function humanBytes(b: number): string {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
  if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
  return `${b} bytes`;
}
